// GL11 순수 변환 로직 — request 비의존 모듈.
// 테스트에서 request 실모듈(okta-auth-js→@peculiar/webcrypto CJS)을 끌고 오면 CI Node 에서 깨진다 —
// 경계에서 끊는 레포 선례(services/inventory apis.test) 준수. 여기에는 부수효과 없는 매핑만 둔다.
import { formatDateTime } from '@/pages/master/userManagement/constants'
import { GL11_CLOSING_RANGE_MAX_DAYS, GL11_MILLISECONDS_PER_DAY } from '../components/consts'

/** BE LocalDateTime 직렬화 형태 — @JsonFormat 없으면 [yyyy,MM,dd,HH,mm,ss,(nano)] 배열로 옴 */
export type ApiDateTime = string | number[] | null

export interface GlobalMonitoringRow {
  /** 저장(1.0 GL11_UpdateForm partial 대응) 대상 식별자 */
  _id: number
  /** 덮어쓰기 방지용 조회 시점 토큰. 표에는 노출하지 않는다. */
  _lastModifiedAt: string | null
  /** 같은 초의 변경도 감지하는 BE opaque 행 버전. */
  _rowVersion: string | null
  ROW_TYPE: string
  ORD_STATUS: string
  CLS_DATE: string
  REF_LSP1: string
  COM_NM: string
  INVOICE_NO: string
  ORD_REMARK: string
  ACT_QTY: number | null
  SPR_ORD_NO: string
  SPR_ORD_OPT_NO: string
  INVOICE_DIFF: string
  GDS_NM: string
  GDS_OPT: string
  ORD_QTY: number | null
  BRD_NM: string
  ISSUE_REASON: string
  CONFIRM_DESC: string
  ACTION_DESC: string
  BRD_CONTACT: string
  INTL_CHK_STAT: string
  INTL_CHK_DATE: string
  CENTER_CHK: string
  CENTER_DESC: string
  CENTER_LOC: string
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
}

export interface GlobalMonitoringParams {
  shipperCode?: string
  /** 최초일자 범위 [from, to] (YYYY-MM-DD) — 필수 필터 */
  closingDateFrom?: string
  closingDateTo?: string
  /** 송장번호 — 다건 IN (서버 지원) */
  invoiceNos?: string[]
  /** 분류 eq */
  status?: string
  /** 주문번호 — 다건 IN (서버 지원) */
  shipperOrderNos?: string[]
}

export interface MonitoringItem {
  id: number
  shipperCode?: string | null
  rowColor?: string | null
  orderStatus?: string | null
  closingDate?: ApiDateTime
  deliveryCompany?: string | null
  senderName?: string | null
  invoiceNo?: string | null
  status?: string | null
  shipperOrderNo?: string | null
  shipperOrderOptionNo?: string | null
  actualInvoiceNo?: string | null
  goodsName?: string | null
  goodsOption?: string | null
  orderQuantity?: number | null
  brandName?: string | null
  issueReason?: string | null
  confirmDescription?: string | null
  actionDescription?: string | null
  brandContact?: string | null
  internalCheckStatus?: string | null
  internalCheckDate?: string | null
  centerChecked?: boolean | null
  centerDescription?: string | null
  centerLocation?: string | null
  createdBy?: string | null
  createdAt?: ApiDateTime
  lastModifiedBy?: string | null
  lastModifiedAt?: ApiDateTime
  rowVersion?: string | null
}

export interface MonitoringHistoryRow {
  CLS_DATE: string
  INVOICE_NO: string
  ORD_REMARK: string
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
}

export interface HistoryItem {
  id: number
  closingDate?: ApiDateTime
  invoiceNo?: string | null
  status?: string | null
  createdAt?: ApiDateTime
  createdBy?: string | null
  lastModifiedAt?: ApiDateTime
  lastModifiedBy?: string | null
}

export const fmtDateTime = (v?: ApiDateTime): string => (v == null ? '' : formatDateTime(v))
export const fmtDate = (v?: ApiDateTime): string => (v == null ? '' : formatDateTime(v).slice(0, 10))

/** BE LocalDateTime 값을 저장 요청에 되돌려 보낼 ISO 토큰으로 보존한다. */
export const toLocalDateTimeToken = (v?: ApiDateTime): string | null => {
  if (v == null) return null
  if (typeof v === 'string') return v.replace(' ', 'T')
  const [year, month, day, hour = 0, minute = 0, second = 0, nano] = v
  const base = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
  return nano == null || nano === 0 ? base : `${base}.${String(nano).padStart(9, '0')}`
}

/** BE boolean → 화면 Y/N (미지정은 빈 값) */
export const centerCheckedToFlag = (v?: boolean | null): string => {
  if (v == null) return ''
  return v ? 'Y' : 'N'
}

/** 화면 Y/N → BE boolean (그 외 값은 미지정 null) */
export const flagToCenterChecked = (v: string): boolean | null => {
  if (v === 'Y') return true
  if (v === 'N') return false
  return null
}

/** 2.0 HEAD 조회 응답 item → 1.0 그리드 행 (변환 산출물 컬럼 키 보존) */
export const toMonitoringRow = (i: MonitoringItem): GlobalMonitoringRow => ({
  _id: i.id,
  _lastModifiedAt: toLocalDateTimeToken(i.lastModifiedAt),
  _rowVersion: i.rowVersion ?? null,
  ROW_TYPE: i.rowColor ?? '',
  ORD_STATUS: i.orderStatus ?? '',
  CLS_DATE: fmtDate(i.closingDate),
  REF_LSP1: i.deliveryCompany ?? '',
  COM_NM: i.senderName ?? '',
  INVOICE_NO: i.invoiceNo ?? '',
  ORD_REMARK: i.status ?? '',
  // 실물수량(ACT_QTY) — 2.0 HEAD 조회 응답에 대응 필드 없음 (LLD-9600 응답 범위 밖, BE 협의 필요 — PR 잔여 항목)
  ACT_QTY: null,
  SPR_ORD_NO: i.shipperOrderNo ?? '',
  SPR_ORD_OPT_NO: i.shipperOrderOptionNo ?? '',
  INVOICE_DIFF: i.actualInvoiceNo ?? '',
  GDS_NM: i.goodsName ?? '',
  GDS_OPT: i.goodsOption ?? '',
  ORD_QTY: i.orderQuantity ?? null,
  BRD_NM: i.brandName ?? '',
  ISSUE_REASON: i.issueReason ?? '',
  CONFIRM_DESC: i.confirmDescription ?? '',
  ACTION_DESC: i.actionDescription ?? '',
  BRD_CONTACT: i.brandContact ?? '',
  INTL_CHK_STAT: i.internalCheckStatus ?? '',
  INTL_CHK_DATE: i.internalCheckDate ?? '',
  CENTER_CHK: centerCheckedToFlag(i.centerChecked),
  CENTER_DESC: i.centerDescription ?? '',
  CENTER_LOC: i.centerLocation ?? '',
  INS_DATE: fmtDateTime(i.createdAt),
  INS_BY: i.createdBy ?? '',
  UPD_DATE: fmtDateTime(i.lastModifiedAt),
  UPD_BY: i.lastModifiedBy ?? '',
})

/** 2.0 /histories 응답 item → 하단(송장 이력) 행 */
export const toHistoryRow = (i: HistoryItem): MonitoringHistoryRow => ({
  CLS_DATE: fmtDate(i.closingDate),
  INVOICE_NO: i.invoiceNo ?? '',
  ORD_REMARK: i.status ?? '',
  INS_DATE: fmtDateTime(i.createdAt),
  INS_BY: i.createdBy ?? '',
  UPD_DATE: fmtDateTime(i.lastModifiedAt),
  UPD_BY: i.lastModifiedBy ?? '',
})

/** 합포 팬아웃으로 같은 _id 가 여러 행일 수 있어 저장 전 _id 로 dedup (서버는 중복 id 400) */
export const dedupeById = <T extends { _id: number }>(rows: T[]): T[] => [...new Map(rows.map((r) => [r._id, r])).values()]

/** 저장 PUT rows 항목 — CS 편집 화이트리스트 11필드 전체 치환 (빈 문자열은 null 로 미지정) */
export const toUpdateItem = (r: GlobalMonitoringRow): Record<string, unknown> => ({
  id: r._id,
  expectedLastModifiedAt: r._lastModifiedAt,
  expectedRowVersion: r._rowVersion,
  status: r.ORD_REMARK || null,
  actualInvoiceNo: r.INVOICE_DIFF || null,
  issueReason: r.ISSUE_REASON || null,
  confirmDescription: r.CONFIRM_DESC || null,
  actionDescription: r.ACTION_DESC || null,
  brandContact: r.BRD_CONTACT || null,
  internalCheckStatus: r.INTL_CHK_STAT || null,
  internalCheckDate: r.INTL_CHK_DATE || null,
  centerChecked: flagToCenterChecked(r.CENTER_CHK),
  centerDescription: r.CENTER_DESC || null,
  centerLocation: r.CENTER_LOC || null,
})

/**
 * 다운로드 사유 모달 입력 → 감사용 사유 문자열. 1.0 RemarkPop REMARK(`{사유코드}_{상세사유}`) 동치 —
 * ST02 BE resolveReason 과 같은 형태라 감사로그(remark)에서 사유코드가 유실되지 않는다.
 */
export const toDetailedReason = ({ reasonCode, reasonDetail }: { reasonCode: string; reasonDetail: string }): string =>
  `${reasonCode}_${reasonDetail}`

/** 엑셀 다운로드 query string — 1.0 파리티(필터 전체) + 다운로드 사유, 다건은 반복 파라미터. 값 없으면 빈 문자열 */
export const buildExcelQuery = (params: GlobalMonitoringParams, detailedReason?: string): string => {
  const entries: [string, unknown][] = [
    ['shipperCode', params.shipperCode],
    ['status', params.status],
    ['closingDateFrom', params.closingDateFrom],
    ['closingDateTo', params.closingDateTo],
  ]
  const qsParts: string[] = []
  entries.forEach(([k, v]) => {
    if (v) qsParts.push(`${k}=${encodeURIComponent(String(v))}`)
  })
  params.invoiceNos?.forEach((v) => qsParts.push(`invoiceNos=${encodeURIComponent(v)}`))
  params.shipperOrderNos?.forEach((v) => qsParts.push(`shipperOrderNos=${encodeURIComponent(v)}`))
  // 다운로드 사유 — BE 가 EXPORT 감사로그(remark)에 남긴다 (LOGISPRD-9602, optional 계약)
  if (detailedReason) qsParts.push(`detailedReason=${encodeURIComponent(detailedReason)}`)
  if (!qsParts.length) return ''
  return `?${qsParts.join('&')}`
}

/**
 * ITEM(송장 이력) 엑셀 다운로드 query string — /histories 조회와 동일 키(화주 + 선택 HEAD 행 송장번호) + 다운로드 사유.
 * 1.0 GL11_ITEM 그리드 자체 엑셀(TOOL_EXP=1) 파리티 — BE GET /histories/excel (LOGISPRD-9602)
 */
export const buildHistoriesExcelQuery = (shipperCode: string, invoiceNo: string, closingDate?: string, detailedReason?: string): string => {
  const qsParts = [`shipperCode=${encodeURIComponent(shipperCode)}`, `invoiceNo=${encodeURIComponent(invoiceNo)}`]
  if (closingDate) qsParts.push(`closingDate=${encodeURIComponent(closingDate)}`)
  // 다운로드 사유 — BE 가 EXPORT 감사로그(remark, apiName=GL11_ITEM)에 남긴다 (optional 계약)
  if (detailedReason) qsParts.push(`detailedReason=${encodeURIComponent(detailedReason)}`)
  return `?${qsParts.join('&')}`
}

/** Content-Disposition → 파일명 (RFC 5987 UTF-8'' 인코딩/따옴표 대응, 실패 시 기본명) */
export const parseExcelFileName = (disposition: string, fallback = '모니터링CS.xlsx'): string => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition)
  return match ? decodeURIComponent(match[1]) : fallback
}

/** 액션바 목록 검색 — 화면 표시 필드 부분 일치(대소문자 무시), 빈 검색어는 전체 통과 */
const toSearchableText = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : ''

export const matchesQuery = (row: Record<string, unknown>, q: string): boolean =>
  !q || Object.entries(row).some(([key, v]) => !key.startsWith('_') && toSearchableText(v).toLowerCase().includes(q.toLowerCase()))

/** BE meta.message 우선, 빈 body 응답(게이트웨이/인프라 차단 등)은 HTTP 상태를 함께 노출 */
export const apiErrMessage = (e: unknown): string => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  return resp?.data?.meta?.message ?? (resp?.status ? `처리에 실패했습니다. (HTTP ${resp.status})` : '처리에 실패했습니다.')
}

export const MONITORING_QUERY_TIMEOUT_MESSAGE = '조회 시간이 초과되었습니다. 기간이나 조건을 줄여 다시 조회해 주세요.'
export const MONITORING_DOWNLOAD_TIMEOUT_MESSAGE = '다운로드 시간이 초과되었습니다. 기간이나 조건을 줄여 다시 시도해 주세요.'

/** axios 는 제한시간 초과를 ECONNABORTED 또는 `timeout of ... exceeded` 메시지로 돌려준다. */
export const isAxiosTimeout = (e: unknown): boolean => {
  const error = e as { code?: unknown; message?: unknown } | null | undefined
  return error?.code === 'ECONNABORTED' || (typeof error?.message === 'string' && /timeout/i.test(error.message))
}

/**
 * 조회 타임아웃만 전역/로컬 쿼리 실패 처리기가 읽는 axios 응답 모양으로 바꾼다.
 * 일반 오류는 객체 자체를 보존해 기존 BE 문구·HTTP 폴백 동작을 바꾸지 않는다.
 */
export const toMonitoringQueryError = (e: unknown): unknown => {
  if (!isAxiosTimeout(e)) return e
  return Object.assign(new Error(MONITORING_QUERY_TIMEOUT_MESSAGE), {
    cause: e,
    response: { data: { meta: { message: MONITORING_QUERY_TIMEOUT_MESSAGE } } },
  })
}

// FilterArea 폼 값(필드 id 키) → 2.0 API 파라미터.
// FormTextArea(다건 입력)의 폼 값은 string[] — 송장/주문번호는 서버 IN 파라미터로 그대로 전달
export const asList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v) && v.length && typeof v[0] !== 'object') return v.map(String)
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}

export const asDate = (v: unknown): string | undefined => {
  if (v && typeof v === 'object' && 'format' in (v as object)) return (v as { format: (f: string) => string }).format('YYYY-MM-DD')
  return typeof v === 'string' && v ? v.slice(0, 10) : undefined
}

/** 최초일자(마감일 구간) 양끝이 모두 있는지 — 조회 제출 필수 검증 (BE 대량 가드 LLD-9600 취지와 정합) */
export const hasClosingRange = (params: GlobalMonitoringParams): boolean => !!(params.closingDateFrom && params.closingDateTo)

/** 시작·종료 포함 최대 31일이며 역전되지 않은 범위인지 검사한다. */
export const isClosingRangeWithinLimit = (params: GlobalMonitoringParams): boolean => {
  if (!params.closingDateFrom || !params.closingDateTo) return false
  const from = Date.parse(`${params.closingDateFrom}T00:00:00Z`)
  const to = Date.parse(`${params.closingDateTo}T00:00:00Z`)
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return false
  return (to - from) / GL11_MILLISECONDS_PER_DAY < GL11_CLOSING_RANGE_MAX_DAYS
}

/**
 * 최초일자 미지정 복원 조건에 오늘 범위를 채운다 — 폼 표시값만 보정하고 자동 조회하지 않는다.
 * menuOnSession/URL 복원값에 날짜가 있으면 그대로 두고, 없으면(구버전 저장분·조작 URL) 오늘로 보정한다.
 * 픽커 기본값(defaultPeriod 'today')과 같은 값이라 표시-조회가 어긋나지 않는다.
 */
export const withDefaultClosingRange = (params: GlobalMonitoringParams, today: string): GlobalMonitoringParams =>
  hasClosingRange(params) ? params : { ...params, closingDateFrom: today, closingDateTo: today }

export const toSearchParams = (values: Record<string, unknown>): GlobalMonitoringParams => {
  const range = values['f-clsDate'] as unknown[] | undefined
  return {
    shipperCode: (values['f-shipper'] as string) || undefined,
    closingDateFrom: Array.isArray(range) ? asDate(range[0]) : undefined,
    closingDateTo: Array.isArray(range) ? asDate(range[1]) : undefined,
    invoiceNos: asList(values['f-invoiceNo']),
    status: (values['f-ordRemark'] as string) && values['f-ordRemark'] !== '__ALL__' ? (values['f-ordRemark'] as string) : undefined,
    shipperOrderNos: asList(values['f-sprOrdNo']),
  }
}

// ── 검색조건 저장/복원 (menuOnSession — ST09 재고현황 searchQuery.ts 준용) ──────────────
// 마지막 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
// 저장 형태는 GlobalMonitoringParams 와 마지막 페이지 — Utils.qsStringify(comma) ↔ useParsedQuery(comma) 왕복.
// 다건은 항목이 하나면 파서가 문자열을 주므로 asList 로 양쪽을 모두 받는다.

const strOrUndef = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

/** URL 쿼리의 마지막 적용 페이지. 조작된 값은 첫 페이지로 보정한다. */
export const fromSearchPage = (query: Record<string, unknown>): number => {
  const raw = query.page
  const page = typeof raw === 'number' ? raw : typeof raw === 'string' && /^\d+$/.test(raw) ? Number(raw) : 1
  return Number.isSafeInteger(page) && page > 0 ? page : 1
}

/**
 * URL 쿼리(파싱 결과) → 복원 검색조건. 화주(필수 필터)가 없으면 복원 대상이 아니다(undefined).
 * 조작된 URL 이 폼/쿼리를 깨뜨리지 않도록 타입이 맞는 값만 받는다.
 */
export const fromSearchQuery = (query: Record<string, unknown>): GlobalMonitoringParams | undefined => {
  const shipperCode = strOrUndef(query.shipperCode)
  if (!shipperCode) return undefined
  return {
    shipperCode,
    closingDateFrom: strOrUndef(query.closingDateFrom),
    closingDateTo: strOrUndef(query.closingDateTo),
    invoiceNos: asList(query.invoiceNos),
    status: strOrUndef(query.status),
    shipperOrderNos: asList(query.shipperOrderNos),
  }
}

/**
 * 복원 검색조건 → FilterArea 폼 값(필드 id 키). 값이 있는 필드만 싣는다(필드 기본값 보존).
 * 기간(f-clsDate)은 RangePicker 가 dayjs 를 요구하므로 소비 측이 toDate(dayjs 변환)를 주입한다.
 */
export const toFilterFormValues = (params: GlobalMonitoringParams, toDate: (d: string) => unknown = (d) => d): Record<string, unknown> => {
  const values: Record<string, unknown> = {}
  if (params.shipperCode) values['f-shipper'] = params.shipperCode
  if (params.closingDateFrom && params.closingDateTo) {
    values['f-clsDate'] = [toDate(params.closingDateFrom), toDate(params.closingDateTo)]
  }
  if (params.invoiceNos?.length) values['f-invoiceNo'] = params.invoiceNos
  if (params.status) values['f-ordRemark'] = params.status
  if (params.shipperOrderNos?.length) values['f-sprOrdNo'] = params.shipperOrderNos
  return values
}
